import { useState, useEffect } from 'react';

const STATUS_CONFIG = {
  active:     { dot: 'bg-green-400' },
  connecting: { dot: 'bg-yellow-400 animate-pulse' },
  error:      { dot: 'bg-red-400' },
  inactive:   { dot: 'bg-white/30' },
};

export default function ETLDebugPanel({ customers = [], connectingId }) {
  const [collapsed, setCollapsed] = useState(true);
  const errorCount = customers.filter((c) => c.status === 'error').length;

  useEffect(() => {
    if (errorCount > 0) setCollapsed(false);
  }, [errorCount]);

  if (customers.length === 0) return null;

  return (
    <div className="flex-shrink-0 border-t border-white/10 bg-black/10 backdrop-blur-sm">
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-2 text-white/60 hover:text-white/90 transition-colors text-xs"
      >
        <span className="flex items-center gap-2 font-medium">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 0 1 1.06 0Z" />
          </svg>
          Connection Status
          {errorCount > 0 && (
            <span className="bg-red-500/80 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold">
              {errorCount} error{errorCount > 1 ? 's' : ''}
            </span>
          )}
        </span>
        <svg
          className={`w-3.5 h-3.5 transition-transform ${collapsed ? '' : 'rotate-180'}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {!collapsed && (
        <div className="px-6 pb-3 flex flex-wrap gap-3">
          {customers.map((c) => {
            const cfg = STATUS_CONFIG[c.status] ?? STATUS_CONFIG.inactive;
            return (
              <div
                key={c.customer_id}
                className={`flex-shrink-0 bg-white/10 rounded-lg px-3 py-2 min-w-[200px] max-w-[340px] border ${
                  c.status === 'error' ? 'border-red-400/40' : 'border-white/5'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                  <span className="text-white text-xs font-semibold truncate">{c.display_name}</span>
                  <span className="ml-auto text-white/40 text-[10px] font-mono">:{c.port}</span>
                </div>

                <div className="flex gap-3 text-[10px] text-white/50">
                  <span>
                    vpn:{' '}
                    <span className={c.vpn_status === 'connected' ? 'text-green-300' : 'text-white/40'}>
                      {c.vpn_status ?? '—'}
                    </span>
                  </span>
                  <span>
                    db:{' '}
                    <span className={c.db_status === 'connected' ? 'text-green-300' : 'text-white/40'}>
                      {c.db_status ?? '—'}
                    </span>
                  </span>
                </div>

                {c.error && (
                  <p className="mt-1.5 text-[10px] text-red-300 leading-relaxed break-words">
                    {c.error}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
